import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Zap, 
  Lock,
  TrendingUp,
  Activity,
  Target,
  Heart,
  Flame,
  FileText,
  Users
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export default function Preview() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    const data = sessionStorage.getItem("previewAnalysis");
    if (data) {
      setAnalysis(JSON.parse(data));
    } else {
      navigate("/");
    }
  }, [navigate]);

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-volt"></div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = analysis.raw_data?.power_data?.map((power, index) => ({
    time: index,
    power: power,
    hr: analysis.raw_data?.hr_data?.[index] || null
  })) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-8 w-8 text-volt" />
              <span className="font-display font-bold text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">by Loops Cycling Academy</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/register">
                <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-6">
                  <span>Vollständige Analyse</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="data-label mb-2">Vorschau</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase">
              Deine <span className="text-volt">Test-Analyse</span>
            </h1>
            <p className="text-muted-foreground mt-2">
              Registriere dich für die vollständige Analyse mit allen Details
            </p>
          </div>

          {/* Main Metrics - Visible */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="glass-card rounded-xl p-6 text-center border border-volt/30">
              <p className="data-label mb-2">FTP</p>
              <p className="number-display text-4xl text-volt">{analysis.ftp}</p>
              <p className="text-sm text-muted-foreground">Watt</p>
            </div>
            <div className="glass-card rounded-xl p-6 text-center border border-cyan/30">
              <p className="data-label mb-2">VO2max</p>
              <p className="number-display text-4xl text-cyan">{analysis.vo2max}</p>
              <p className="text-sm text-muted-foreground">ml/kg/min</p>
              {analysis.estimated_weight && (
                <p className="text-xs text-muted-foreground mt-1">(bei {analysis.estimated_weight}kg)</p>
              )}
            </div>
          </div>

          {/* VT1/VT2 - Blurred */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="glass-card rounded-xl p-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex items-center justify-center">
                <Lock className="h-5 w-5 text-volt" />
              </div>
              <p className="data-label mb-2">VT1</p>
              <p className="number-display text-4xl text-carb-orange">???</p>
              <p className="text-sm text-muted-foreground">Watt</p>
            </div>
            <div className="glass-card rounded-xl p-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex items-center justify-center">
                <Lock className="h-5 w-5 text-volt" />
              </div>
              <p className="data-label mb-2">VT2</p>
              <p className="number-display text-4xl text-heart-red">???</p>
              <p className="text-sm text-muted-foreground">Watt</p>
            </div>
          </div>

          {/* Power Curve Chart - Visible without axis values */}
          <div className="glass-card rounded-xl p-6 mb-8">
            <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
              <Activity className="h-5 w-5 text-volt" />
              Testverlauf
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="time" 
                    tick={false}
                    axisLine={{ stroke: '#333' }}
                  />
                  <YAxis 
                    yAxisId="power"
                    tick={false}
                    axisLine={{ stroke: '#333' }}
                    domain={['auto', 'auto']}
                  />
                  <YAxis 
                    yAxisId="hr"
                    orientation="right"
                    tick={false}
                    axisLine={{ stroke: '#333' }}
                    domain={['auto', 'auto']}
                  />
                  {/* FTP Line */}
                  <ReferenceLine 
                    yAxisId="power"
                    y={analysis.ftp} 
                    stroke="#D4FF00" 
                    strokeDasharray="8 4" 
                    strokeWidth={2}
                  />
                  {/* VT1 Line */}
                  <ReferenceLine 
                    yAxisId="power"
                    y={analysis.vt1} 
                    stroke="#FFAA00" 
                    strokeDasharray="5 5" 
                    strokeWidth={1.5}
                  />
                  {/* VT2 Line */}
                  <ReferenceLine 
                    yAxisId="power"
                    y={analysis.vt2} 
                    stroke="#FF0055" 
                    strokeDasharray="5 5" 
                    strokeWidth={1.5}
                  />
                  <Line 
                    yAxisId="power"
                    type="monotone" 
                    dataKey="power" 
                    stroke="#D4FF00" 
                    strokeWidth={2}
                    dot={false}
                    name="Leistung"
                  />
                  {analysis.raw_data?.hr_data?.length > 0 && (
                    <Line 
                      yAxisId="hr"
                      type="monotone" 
                      dataKey="hr" 
                      stroke="#FF0055" 
                      strokeWidth={1.5}
                      dot={false}
                      name="Herzfrequenz"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-volt"></div>
                <span className="text-muted-foreground">Leistung</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-heart-red"></div>
                <span className="text-muted-foreground">Herzfrequenz</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 border-t-2 border-dashed border-volt"></div>
                <span className="text-muted-foreground">FTP</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 border-t-2 border-dashed border-carb-orange"></div>
                <span className="text-muted-foreground">VT1</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 border-t-2 border-dashed border-heart-red"></div>
                <span className="text-muted-foreground">VT2</span>
              </div>
            </div>
          </div>

          {/* Blurred Sections */}
          <div className="space-y-6">
            {/* Power Zones - Blurred */}
            <div className="glass-card rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex flex-col items-center justify-center">
                <Lock className="h-8 w-8 text-volt mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Registriere dich für alle Details</p>
                <Link to="/register">
                  <Button size="sm" className="bg-volt text-black hover:bg-volt/90 font-bold">
                    Jetzt freischalten
                  </Button>
                </Link>
              </div>
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-volt" />
                Leistungszonen
              </h3>
              <div className="grid grid-cols-7 gap-2 opacity-30">
                {Object.entries(analysis.power_zones || {}).map(([key, zone]) => (
                  <div key={key} className="text-center p-3 rounded-lg bg-zinc-900">
                    <p className="text-xs font-medium mb-1">{zone.name?.split(' ')[0]}</p>
                    <p className="text-lg font-bold text-volt">???</p>
                    <p className="text-xs text-muted-foreground">Watt</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Heart Rate Zones - Blurred */}
            <div className="glass-card rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex flex-col items-center justify-center">
                <Lock className="h-8 w-8 text-volt mb-3" />
                <p className="text-sm text-muted-foreground">Vollständige Analyse erforderlich</p>
              </div>
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-heart-red" />
                Herzfrequenzzonen
              </h3>
              <div className="grid grid-cols-5 gap-2 opacity-30">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="text-center p-3 rounded-lg bg-zinc-900">
                    <p className="text-xs font-medium mb-1">Zone {i}</p>
                    <p className="text-lg font-bold text-heart-red">???</p>
                    <p className="text-xs text-muted-foreground">bpm</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Carb Consumption - Blurred */}
            <div className="glass-card rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex flex-col items-center justify-center">
                <Lock className="h-8 w-8 text-volt mb-3" />
                <p className="text-sm text-muted-foreground">Vollständige Analyse erforderlich</p>
              </div>
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <Flame className="h-5 w-5 text-carb-orange" />
                Kohlenhydratverbrauch
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 opacity-30">
                {[100, 150, 200, 250, 300, 350].map((watt) => (
                  <div key={watt} className="text-center p-3 rounded-lg bg-zinc-900">
                    <p className="text-xs font-medium mb-1">{watt}W</p>
                    <p className="text-lg font-bold text-carb-orange">??</p>
                    <p className="text-xs text-muted-foreground">g/h</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Peer Comparison - Blurred */}
            <div className="glass-card rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex flex-col items-center justify-center">
                <Lock className="h-8 w-8 text-volt mb-3" />
                <p className="text-sm text-muted-foreground">Vollständige Analyse erforderlich</p>
              </div>
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-cyan" />
                Peer-Group Vergleich
              </h3>
              <div className="flex justify-around opacity-30">
                <div className="text-center">
                  <p className="text-2xl font-bold text-cyan">???</p>
                  <p className="text-xs text-muted-foreground">Kategorie</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-cyan">???</p>
                  <p className="text-xs text-muted-foreground">Perzentile</p>
                </div>
              </div>
            </div>

            {/* AI Assessment - Blurred */}
            <div className="glass-card rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex flex-col items-center justify-center">
                <Lock className="h-8 w-8 text-volt mb-3" />
                <p className="text-sm text-muted-foreground">Vollständige Analyse erforderlich</p>
              </div>
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-volt" />
                Einstufung der Leistung
              </h3>
              <div className="opacity-30">
                <div className="h-32 bg-zinc-900 rounded-lg flex items-center justify-center">
                  <p className="text-muted-foreground">KI-generierte Bewertung...</p>
                </div>
              </div>
            </div>

            {/* PDF Report - Blurred */}
            <div className="glass-card rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-md bg-background/60 z-10 flex flex-col items-center justify-center">
                <Lock className="h-8 w-8 text-volt mb-3" />
                <p className="text-sm text-muted-foreground">Vollständige Analyse erforderlich</p>
              </div>
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-volt" />
                PDF Report
              </h3>
              <div className="opacity-30">
                <Button disabled className="w-full">PDF herunterladen</Button>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center glass-card rounded-xl p-8 border border-volt/30">
            <h2 className="font-display text-2xl font-bold uppercase mb-4">
              Möchtest du <span className="text-volt">alle Details</span> sehen?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Registriere dich jetzt und erhalte Zugang zu allen Leistungszonen, 
              KI-Bewertung, Peer-Vergleich und professionellem PDF-Report.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-8">
                  <span>Jetzt registrieren</span>
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-white/20 hover:bg-white/10 px-8">
                  Preise ansehen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
